import {
  addDoc,
  arrayUnion,
  collection,
  deleteDoc,
  doc,
  increment,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db } from '../firebase/config';

const COLLECTION = 'newsComments';
const THREAD_PAGE_SIZE = 200;
const MAX_LENGTH = 1000;

// Listens to a news post's comment thread. Visibility mirrors the pin-wall:
//   - Mods (canModerate=true): every comment regardless of status.
//   - Logged-in non-mods: visible comments + any hidden ones the viewer themself reported.
//   - Anonymous: only visible comments.
// Two-listener merge for non-mods because Firestore queries can't OR across statuses,
// and the rules forbid a non-mod from even reading other people's hidden/removed docs.
export const listenToNewsComments = (newsId, callback, options = {}) => {
  const { canModerate = false, currentUid = null, onError } = options;
  if (!newsId) {
    callback([]);
    return () => {};
  }

  const handleErr = (err) => {
    if (onError) onError(err);
    else console.error('listenToNewsComments:', err);
  };

  if (canModerate) {
    const q = query(
      collection(db, COLLECTION),
      where('newsId', '==', newsId),
      orderBy('createdAt', 'asc'),
      limit(THREAD_PAGE_SIZE)
    );
    return onSnapshot(
      q,
      (snap) => callback(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      handleErr
    );
  }

  let visibleRows = [];
  let reportedRows = [];
  // The two listeners query disjoint statuses ('visible' vs 'hidden'), so a
  // concat can never duplicate a doc — no dedupe needed.
  const emit = () => {
    const merged = [...visibleRows, ...reportedRows];
    merged.sort((a, b) => {
      const am = a.createdAt?.toMillis?.() ?? 0;
      const bm = b.createdAt?.toMillis?.() ?? 0;
      return am - bm;
    });
    callback(merged);
  };

  const visibleQ = query(
    collection(db, COLLECTION),
    where('newsId', '==', newsId),
    where('status', '==', 'visible'),
    orderBy('createdAt', 'asc'),
    limit(THREAD_PAGE_SIZE)
  );
  const unsubVisible = onSnapshot(
    visibleQ,
    (snap) => {
      visibleRows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      emit();
    },
    handleErr
  );

  let unsubReported = () => {};
  if (currentUid) {
    const reporterQ = query(
      collection(db, COLLECTION),
      where('newsId', '==', newsId),
      where('status', '==', 'hidden'),
      where('reportedBy', 'array-contains', currentUid),
      orderBy('createdAt', 'asc'),
      limit(THREAD_PAGE_SIZE)
    );
    unsubReported = onSnapshot(
      reporterQ,
      (snap) => {
        reportedRows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        emit();
      },
      handleErr
    );
  }

  return () => {
    unsubVisible();
    unsubReported();
  };
};

export const addNewsComment = async ({ newsId, parentId = null, author, text }) => {
  if (!newsId) throw new Error('newsId is required');
  if (!author?.uid) throw new Error('author.uid is required');
  const trimmed = (text || '').trim();
  if (!trimmed) throw new Error('Comment cannot be empty.');
  if (trimmed.length > MAX_LENGTH)
    throw new Error(`Comment is too long (max ${MAX_LENGTH} characters).`);

  const ref = await addDoc(collection(db, COLLECTION), {
    newsId,
    parentId: parentId || null,
    authorUserId: author.uid,
    authorUsername: author.username || 'Unknown',
    authorPhotoURL: author.photoURL || null,
    text: trimmed,
    createdAt: serverTimestamp(),
    reportCount: 0,
    reportedBy: [],
    status: 'visible',
    removedBy: null,
    removedAt: null,
  });
  return ref.id;
};

export const deleteComment = (commentId) => deleteDoc(doc(db, COLLECTION, commentId));

export const reportComment = (commentId, uid) => {
  if (!commentId || !uid) throw new Error('commentId and uid are required');
  return updateDoc(doc(db, COLLECTION, commentId), {
    status: 'hidden',
    reportCount: increment(1),
    reportedBy: arrayUnion(uid),
  });
};

export const listenToNewsModerationQueue = (callback, onError) => {
  const q = query(
    collection(db, COLLECTION),
    where('status', '==', 'hidden'),
    orderBy('reportCount', 'desc'),
    orderBy('createdAt', 'desc')
  );
  return onSnapshot(
    q,
    (snap) => callback(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    (err) => {
      if (onError) onError(err);
      else console.error('listenToNewsModerationQueue:', err);
    }
  );
};

export const restoreComment = (commentId) =>
  updateDoc(doc(db, COLLECTION, commentId), {
    status: 'visible',
    reportCount: 0,
    reportedBy: [],
  });

export const removeComment = (commentId, modUid) =>
  updateDoc(doc(db, COLLECTION, commentId), {
    status: 'removed',
    removedBy: modUid || null,
    removedAt: serverTimestamp(),
  });
